import {
  useGetEvalVersions,
  useGetMaestroVersions,
} from '@automata/api/apiComponents';
import {
  ReferenceDataDefinitions,
  RunParameterDefinitions,
  WorkflowMaestro,
} from '@automata/api/apiSchemas';
import { schemas } from '@automata/api/zod';
import { Typography, defaultTheme, useTheme } from '@automata/mui-theme';
import { Spinner, TextField, TrackedButton } from '@automata/ui';
import { getErrorMessage } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';

import { DriversSelector } from 'components/DriversSelector';
import { EvalsSelector } from 'components/EvalsVersionSelector';
import { MaestroSelector } from 'components/MaestroVersionSelector';
import { useArchetypesVersions } from 'hooks/useArchetypesVersions';
import { useSnackbar } from 'hooks/useSnackbar';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import useUserV2 from 'hooks/useUserV2';
import { AllDriversPackage } from 'model/Archetypes';
import { PermissionLevelsEnum, PermissionsEnum } from 'model/User';
import { EditorProps } from 'model/WorkflowTypes';

import { initialMaestroWorkflow } from './defs/initialState';
import { OverSizedJson } from './overSizedJson';
import { checkSemverVersionCompatibility } from './utils';

export const MaestroPackage = 'maestro';

export type MaestroEditorContent = {
  workflow: WorkflowMaestro;
  parameters: string;
  referenceData: string;
};

const workflowMaestroSchemaWithEvals = schemas.WorkflowMaestro.extend({
  resource_versions: zod.object({
    [MaestroPackage]: zod.string().min(1),
    [AllDriversPackage]: zod.string().min(1),
    evals_version: zod.string().min(1),
    evals_org: zod.string(),
  }),
});

const maestroEditorSchema = zod.object({
  workflow: schemas.WorkflowMaestro,
  parameters: zod.string(),
  referenceData: zod.string(),
});

const maestroEditorSchemaWithEvals = maestroEditorSchema.extend({
  workflow: workflowMaestroSchemaWithEvals,
});

export const MaestroEditor = ({
  requestData,
  updateData,
  onContentUpdated,
  useWorkcellTransportConfig = false,
  onUseWorkcellTransportConfigChange,
}: EditorProps): JSX.Element => {
  const [compatibleDriversVersionFilter, setCompatibleDriversVersionFilter] =
    useState<string>('v*');

  const { data } = useUserV2();

  const isUserHaveCreatorAccess = useMemo(
    () =>
      data?.permissions.get(PermissionsEnum.RUN_RESOURCES)?.level ===
      PermissionLevelsEnum.WRITE,
    [data]
  );

  const { enqueueSnackbar } = useSnackbar();
  const [queryParams, setQueryParams] = useURLSearchParams();
  const isEditable = queryParams.editable === 'true' && isUserHaveCreatorAccess;

  const { all: archetypesVersions } = useArchetypesVersions();
  const {
    data: maestroVersionsData,
    error: maestroVersionsError,
    isLoading: isMaestroVersionsLoading,
  } = useGetMaestroVersions({});

  const { data: evalVersionsData } = useGetEvalVersions({});

  const isRunParamsEnabled = useFlag('fe.run_params');
  const isEvalsEnabled = useFlag('fe.evals');
  const isTransportConfigHandlingEnabled = useFlag(
    'fe.transport_config_handling'
  );

  if (maestroVersionsError) {
    enqueueSnackbar('Failed to fetch scheduler engine versions', {
      variant: 'error',
    });
  }

  const theme = useTheme();
  const { handleSubmit, reset, control, setValue, formState, getValues } =
    useForm<MaestroEditorContent>({
      defaultValues: {
        workflow: initialMaestroWorkflow,
        parameters: '[]',
        referenceData: '[]',
      },
      resolver: zodResolver(
        isEvalsEnabled ? maestroEditorSchemaWithEvals : maestroEditorSchema
      ),
    });
  const isOversizedJsonEnabled = useFlag('fe.oversized_workflow_json');
  useEffect(() => {
    if (requestData?.definition.maestro) {
      const { workflow, transport, resource_versions } =
        requestData.definition.maestro;

      const workflowConfig =
        typeof workflow === 'string'
          ? workflow
          : JSON.stringify(requestData.definition.maestro.workflow, null, 2);
      const transportConfig =
        typeof transport === 'string'
          ? transport
          : JSON.stringify(requestData.definition.maestro.transport, null, 2);

      const compatibleDriversVersionFilter =
        resource_versions?.[AllDriversPackage];
      if (compatibleDriversVersionFilter) {
        setCompatibleDriversVersionFilter(compatibleDriversVersionFilter);
      }

      reset({
        workflow: {
          ...requestData.definition.maestro,
          workflow: workflowConfig,
          transport: transportConfig,
        },
        parameters: JSON.stringify(
          requestData.attributes?.parameters ?? [],
          null,
          2
        ),
        referenceData: JSON.stringify(
          requestData.attributes?.reference_data_definitions ?? [],
          null,
          2
        ),
      });
    }
  }, [requestData, reset]);

  useEffect(() => {
    if (evalVersionsData) {
      // Check if evalVersionsData is available
      const { org_slug } = evalVersionsData;
      // Set the org_slug value explicitly using setValue
      setValue('workflow.resource_versions.evals_org', org_slug);
    }
  }, [evalVersionsData, setValue]);

  const makeUpdate = async (maestro: MaestroEditorContent) => {
    if (!maestro?.workflow) {
      enqueueSnackbar('Workflow data not found', { variant: 'error' });
      return;
    }

    let parameters: RunParameterDefinitions;
    try {
      parameters = JSON.parse(maestro.parameters);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'failed to parse parameters'), {
        variant: 'error',
      });
      return;
    }

    let referenceData: ReferenceDataDefinitions;
    try {
      referenceData = JSON.parse(maestro.referenceData);
    } catch (error) {
      enqueueSnackbar(
        getErrorMessage(error, 'failed to parse reference data'),
        { variant: 'error' }
      );
      return;
    }

    const { workflow: workflowConfig, ...rest } = maestro.workflow;
    const maestroPayload = isOversizedJsonEnabled
      ? rest
      : {
          ...rest,
          workflow: workflowConfig,
        };

    try {
      const updated = await updateData(
        { maestro: maestroPayload },
        {
          ...requestData?.attributes,
          parameters,
          reference_data_definitions: referenceData,
        },
        requestData?.run_instructions
      );
      onContentUpdated(updated);
      enqueueSnackbar('Workflow updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        `Workflow failed to update: ${getErrorMessage(error, 'failed')}`,
        { variant: 'error' }
      );
    }
  };

  const checkDriversVersionCompatibility = (version: string) =>
    checkSemverVersionCompatibility(version, compatibleDriversVersionFilter);

  if (
    isMaestroVersionsLoading ||
    !archetypesVersions ||
    !maestroVersionsData ||
    !evalVersionsData
  ) {
    return <Spinner size={42} />;
  }
  return (
    <form
      name="scheduler-engine-editor-form"
      onSubmit={handleSubmit(makeUpdate)}
    >
      <Stack
        direction="row"
        gap={2}
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
      >
        <Stack direction="row" gap={2} flexWrap="wrap">
          <Stack direction="row" gap={2} my={2.5} pt={0.3}>
            <FormControlLabel
              control={
                <Switch
                  id="workflow-lock-switch"
                  checked={isEditable}
                  disabled={!isUserHaveCreatorAccess}
                  onChange={() => {
                    setQueryParams({ editable: !isEditable });
                  }}
                />
              }
              label="Edit mode"
              sx={{ mr: 0 }}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ color: theme.palette.divider, my: 0.5 }}
            />
            <Controller
              name="workflow.simulateDrivers"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="simulate-drivers-checkbox"
                      disabled={!isEditable}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Simulate drivers"
                  sx={{ mr: 0 }}
                />
              )}
            />
            <Controller
              name="workflow.simulateTime"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="simulate-time-checkbox"
                      disabled={!isEditable}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Simulate time"
                  sx={{ mr: 0 }}
                />
              )}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ color: theme.palette.divider, my: 0.5 }}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <Controller
              name={`workflow.resource_versions.${MaestroPackage}`}
              control={control}
              render={({ field }) => (
                <MaestroSelector
                  field={field}
                  maestroVersionsData={maestroVersionsData}
                  disabled={!isEditable}
                  setCompatibleDriversVersionFilter={
                    setCompatibleDriversVersionFilter
                  }
                />
              )}
            />
            {isEvalsEnabled && (
              <Controller
                name="workflow.resource_versions.evals_version"
                control={control}
                render={({ field }) => (
                  <EvalsSelector
                    field={field}
                    evalVersionsData={evalVersionsData}
                    disabled={!isEditable}
                  />
                )}
              />
            )}
            <DriversSelector<MaestroEditorContent>
              fullWidth={false}
              name={`workflow.resource_versions.${AllDriversPackage}`}
              control={control}
              archetypesVersions={archetypesVersions}
              disabled={!isEditable}
              compatibilityFilter={checkDriversVersionCompatibility}
            />
          </Stack>
        </Stack>
        <Stack mb={3}>
          <TrackedButton
            trackLabel="wflow-scheduler-engine-save"
            fullWidth={false}
            loading={false}
            variant="contained"
            type="submit"
            disabled={!isEditable || !formState.isValid}
          >
            Save
          </TrackedButton>
        </Stack>
      </Stack>
      <Grid container rowSpacing={1} columnSpacing={1} paddingBottom={2}>
        {(['workflow', 'transport'] as const).map((name) => (
          <Grid key={name} item xs={6}>
            <Controller
              name={`workflow.${name}`}
              control={control}
              render={({ field }) =>
                isOversizedJsonEnabled && name === 'workflow' ? (
                  <Stack width="100%">
                    <Typography
                      component="label"
                      variant="overline"
                      color={defaultTheme.palette.text.primary}
                      pt={1}
                      pb={0.5}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      {`${name} configuration`}
                    </Typography>
                    <OverSizedJson
                      jsonType={name}
                      workflow={{
                        id: requestData?.id || '',
                        name: requestData?.name || '',
                      }}
                      content={getValues().workflow.workflow ?? ''}
                      onChange={(content) =>
                        setValue('workflow.workflow', content, {
                          shouldValidate: true,
                          shouldTouch: true,
                          shouldDirty: true,
                        })
                      }
                      isEditable={isEditable}
                    />
                  </Stack>
                ) : name === 'transport' ? (
                  <Stack width="100%">
                    <Typography
                      component="label"
                      htmlFor={`scheduler-engine-${name}-textfield`}
                      variant="overline"
                      color={defaultTheme.palette.text.primary}
                      pt={1}
                      pb={0.5}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      {`${name} configuration`}
                    </Typography>
                    {isTransportConfigHandlingEnabled && (
                      <FormControlLabel
                        control={
                          <Switch
                            id="workflow-use-workcell-transport-switch"
                            checked={useWorkcellTransportConfig}
                            onChange={(e) =>
                              onUseWorkcellTransportConfigChange?.(
                                e.target.checked
                              )
                            }
                            disabled={!isEditable}
                          />
                        }
                        label="Use workcell transport config"
                        sx={{ mb: 1 }}
                      />
                    )}
                    <TextField
                      {...field}
                      id={`scheduler-engine-${name}-textfield`}
                      multiline
                      fullWidth
                      rows={40}
                      disabled={!isEditable || useWorkcellTransportConfig}
                      InputProps={{
                        sx: {
                          '& textarea': {
                            fontFamily: 'monospace',
                            fontSize: '10px',
                            lineHeight: '1.5',
                          },
                        },
                      }}
                    />
                  </Stack>
                ) : (
                  // TODO: Create a generic json editor text input component.
                  <TextField
                    {...field}
                    id={`scheduler-engine-${name}-textfield`}
                    multiline
                    fullWidth
                    label={`${name} configuration`}
                    rows={40}
                    disabled={!isEditable}
                    InputProps={{
                      sx: {
                        '& textarea': {
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          lineHeight: '1.5',
                        },
                      },
                    }}
                  />
                )
              }
            />
          </Grid>
        ))}
        {isRunParamsEnabled && (
          <>
            <Grid item xs={6}>
              <Controller
                name={'parameters'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="scheduler-engine-parameters-textfield"
                    multiline
                    fullWidth
                    label="parameters configuration"
                    rows={20}
                    disabled={!isEditable}
                    InputProps={{
                      sx: {
                        '& textarea': {
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          lineHeight: '1.5',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name={'referenceData'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="scheduler-engine-referenceData-textfield"
                    multiline
                    fullWidth
                    label="reference data configuration"
                    rows={20}
                    disabled={!isEditable}
                    InputProps={{
                      sx: {
                        '& textarea': {
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          lineHeight: '1.5',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </form>
  );
};
